import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Button,
  Form,
  FormFeedback,
  Alert,
  Spinner,
} from "reactstrap";

import * as Yup from "yup";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { withRouter, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import logoLight from "../../assets/images/logo-light.png";
import { loginUser, resetLoginFlag } from "../../store/actions";
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";

const Login = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [passwordType, setPasswordType] = useState("password");

  const { loading } = useSelector((state) => state.Login);
  const [userLogin] = useState([]);

  /**
   * Formik handling
   */
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: userLogin.email || "",
      password: userLogin.password || "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email(t("Validations.Email.Invalid"))
        .required(t("Validations.Email.Required")),
      password: Yup.string().required(t("Validations.Password.Required")),
    }),

    onSubmit: (values) => {
      dispatch(loginUser(values, props.history));
    },
  });

  const { error } = useSelector((state) => ({
    error: state.Login.error,
  }));

  useEffect(() => {
    setTimeout(() => {
      dispatch(resetLoginFlag());
    }, 3000);
  }, [dispatch, error]);

  /**
   * Password show hide
   */
  const handleClickShowPassword = () => {
    if (passwordType === "text") setPasswordType("password");
    else setPasswordType("text");
  };

  document.title = `${t("Login.Login")} | ${t(process.env.REACT_APP_HEADING)}`;

  return (
    <React.Fragment>
      <ParticlesAuth>
        <div className="auth-page-content">
          <Container>
            <Row>
              <Col lg={12}>
                <div className="text-center mt-sm-5 mb-4 text-white-50">
                  <div>
                    <Link to="/" className="d-inline-block auth-logo">
                      <img src={logoLight} alt="" height="75" />
                    </Link>
                  </div>
                  <p className="mt-3 fs-15 fw-medium">
                    {t("Common.Technical Services at your door step")}
                  </p>
                </div>
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col md={8} lg={6} xl={5}>
                <Card className="mt-4">
                  <CardBody className="p-4">
                    <div className="text-center mt-2">
                      <h5 className="text-primary">
                        {t("Login.Welcome Back")}
                      </h5>
                      <p className="text-muted">
                        {t("Login.Sign in to continue to App Control Panel")}
                      </p>
                    </div>
                    {error && error ? (
                      <Alert color="danger">
                        {" "}
                        {t(`Messages.Error.${error}`)}{" "}
                      </Alert>
                    ) : null}
                    <div className="p-2 mt-4">
                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          validation.handleSubmit();
                          return false;
                        }}
                        action="#"
                      >
                        <div className="mb-3">
                          <Label htmlFor="email" className="form-label">
                            {t("Fields.Email")}
                          </Label>
                          <Input
                            name="email"
                            className="form-control"
                            placeholder={t("Fields.Enter Email")}
                            type="email"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.email || ""}
                            invalid={
                              validation.touched.email &&
                              validation.errors.email
                                ? true
                                : false
                            }
                          />
                          {validation.touched.email &&
                          validation.errors.email ? (
                            <FormFeedback type="invalid">
                              {validation.errors.email}
                            </FormFeedback>
                          ) : null}
                        </div>

                        <div className="mb-3">
                          <div className="float-end">
                            <Link to="/forgot-password" className="text-muted">
                              {t("ForgotPassword.Forgot Password?")}
                            </Link>
                          </div>
                          <Label
                            className="form-label"
                            htmlFor="password-input"
                          >
                            {t("Fields.Password")}
                          </Label>
                          <div className="position-relative auth-pass-inputgroup mb-3">
                            <Input
                              name="password"
                              value={validation.values.password || ""}
                              type={passwordType}
                              className="form-control pe-5"
                              placeholder={t("Fields.Enter Password")}
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              invalid={
                                validation.touched.password &&
                                validation.errors.password
                                  ? true
                                  : false
                              }
                            />
                            {validation.touched.password &&
                            validation.errors.password ? (
                              <FormFeedback type="invalid">
                                {validation.errors.password}
                              </FormFeedback>
                            ) : null}
                            <button
                              className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                              type="button"
                              id="password-addon"
                            >
                              <i
                                className="ri-eye-fill align-middle"
                                onClick={handleClickShowPassword}
                              ></i>
                            </button>
                          </div>
                        </div>

                        <div className="form-check">
                          <Input
                            className="form-check-input"
                            type="checkbox"
                            value=""
                            id="auth-remember-check"
                          />
                          <Label
                            className="form-check-label"
                            htmlFor="auth-remember-check"
                          >
                            {t("Login.Remember me")}
                          </Label>
                        </div>

                        <div className="mt-4">
                          <Button
                            color="success"
                            className="btn-load w-100"
                            type="submit"
                            disabled={loading}
                          >
                            {loading ? (
                              <span className="d-flex align-items-center">
                                <span className="flex-grow-1 me-2">
                                  {t("Login.Authenticating")}...
                                </span>
                                <Spinner
                                  size="sm"
                                  className="flex-shrink-0"
                                  role="status"
                                >
                                  {" "}
                                  {t("Login.Authenticating")}...{" "}
                                </Spinner>
                              </span>
                            ) : (
                              <>{t("Sign In")}</>
                            )}
                          </Button>
                        </div>
                      </Form>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </ParticlesAuth>
    </React.Fragment>
  );
};

export default withRouter(Login);
